/**
 * DialogFikoDelikComponent - Delik İşleme Dialog
 * Profil dialog tarzında kompakt, minimal, SVG tabanlı tasarım
 *
 * 5 Delik Tipi:
 * - Punta (G81): Spot drilling
 * - Delme (G83): Peck drilling
 * - Rayba (G85): Reaming
 * - Kılavuz (G84): Tapping
 * - Havşa (G82): Counterboring
 *
 * @version 2.0.0 - Profil tarzı UI
 */

import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import * as THREE from 'three';
import { FikoAktiviteDelik } from '../FikoAktiviteDelik';
import { FikoVektor3D } from '../../../../FikoVektor3d';
import { FikoSecililer } from '../../../../FikoSecililer';

@Component({
    selector: 'dialog-fiko-delik',
    standalone: true,
    imports: [CommonModule, FormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
    <div class="cam-dialog" *ngIf="aktivite">
        <!-- NEON BORDER -->
        <div class="neon-border"></div>

        <!-- Header -->
        <div class="d-header">
            <div class="header-glow"></div>
            <div class="title-wrap">
                <svg class="title-icon" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="6" fill="none" stroke="url(#delikGrad)" stroke-width="2"/>
                    <circle cx="12" cy="12" r="2" fill="url(#delikGrad)"/>
                    <line x1="12" y1="2" x2="12" y2="6" stroke="url(#delikGrad)" stroke-width="2" stroke-linecap="round"/>
                    <defs>
                        <linearGradient id="delikGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stop-color="#0f0"/><stop offset="100%" stop-color="#0a8"/>
                        </linearGradient>
                    </defs>
                </svg>
                <span class="title">Delik</span>
                <span class="subtitle">{{aktivite.delikNoktalar?.length || 0}} nokta</span>
            </div>
            <button class="close-btn" (click)="kapat()">✕</button>
        </div>

        <!-- Tab Header -->
        <div class="tab-header">
            <button class="tab-btn" [class.active]="aktifTab === 0" (click)="setTab(0)">
                <svg viewBox="0 0 20 20" class="tab-icon">
                    <circle cx="10" cy="10" r="6" fill="none" stroke="currentColor" stroke-width="1.5"/>
                    <circle cx="10" cy="10" r="2" fill="currentColor"/>
                </svg>
                <span>Delik</span>
            </button>
            <button class="tab-btn" [class.active]="aktifTab === 1" (click)="setTab(1)">
                <svg viewBox="0 0 20 20" class="tab-icon">
                    <path d="M10,2 L10,18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    <path d="M6,6 L14,6" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M7,10 L13,10" stroke="currentColor" stroke-width="1.5"/>
                </svg>
                <span>{{aktivite.delikTipi || 'Delme'}}</span>
            </button>
            <button class="tab-btn" [class.active]="aktifTab === 2" (click)="setTab(2)">
                <svg viewBox="0 0 20 20" class="tab-icon">
                    <rect x="8" y="2" width="4" height="14" rx="1" fill="currentColor" opacity="0.7"/>
                    <ellipse cx="10" cy="17" rx="5" ry="2" fill="none" stroke="currentColor" stroke-width="1.5"/>
                </svg>
                <span>Takım</span>
            </button>
        </div>

        <!-- TAB 0: DELİK NOKTALARI & TİP SEÇİMİ -->
        <div class="tab-content" *ngIf="aktifTab === 0">
            <!-- Delik Tipi Seçimi -->
            <div class="type-section">
                <div class="type-buttons">
                    <button class="type-btn" [class.active]="aktivite.delikTipi === 'Punta'" (click)="setDelikTipi('Punta')">
                        <div class="type-icon-wrap">
                            <svg viewBox="0 0 32 32" class="type-icon">
                                <path d="M16,4 L16,24" stroke="#0ff" stroke-width="3" stroke-linecap="round"/>
                                <path d="M12,24 L16,28 L20,24" fill="#0ff"/>
                            </svg>
                        </div>
                        <span class="type-label">Punta</span>
                        <span class="type-code">G81</span>
                    </button>
                    <button class="type-btn" [class.active]="aktivite.delikTipi === 'Delme'" (click)="setDelikTipi('Delme')">
                        <div class="type-icon-wrap">
                            <svg viewBox="0 0 32 32" class="type-icon">
                                <path d="M16,2 L16,10 M16,14 L16,22 M16,26 L16,30" stroke="#0f8" stroke-width="3" stroke-linecap="round"/>
                            </svg>
                        </div>
                        <span class="type-label">Delme</span>
                        <span class="type-code">G83</span>
                    </button>
                    <button class="type-btn" [class.active]="aktivite.delikTipi === 'Rayba'" (click)="setDelikTipi('Rayba')">
                        <div class="type-icon-wrap">
                            <svg viewBox="0 0 32 32" class="type-icon">
                                <rect x="13" y="4" width="6" height="24" rx="1" fill="#f80"/>
                            </svg>
                        </div>
                        <span class="type-label">Rayba</span>
                        <span class="type-code">G85</span>
                    </button>
                    <button class="type-btn" [class.active]="aktivite.delikTipi === 'Kilavuz'" (click)="setDelikTipi('Kilavuz')">
                        <div class="type-icon-wrap">
                            <svg viewBox="0 0 32 32" class="type-icon">
                                <path d="M16,4 L16,28" stroke="#f0f" stroke-width="3"/>
                                <path d="M12,8 L20,12 M12,16 L20,20 M12,24 L20,28" stroke="#f0f" stroke-width="1.5"/>
                            </svg>
                        </div>
                        <span class="type-label">Kılavuz</span>
                        <span class="type-code">G84</span>
                    </button>
                    <button class="type-btn" [class.active]="aktivite.delikTipi === 'Havsa'" (click)="setDelikTipi('Havsa')">
                        <div class="type-icon-wrap">
                            <svg viewBox="0 0 32 32" class="type-icon">
                                <path d="M16,4 L16,20" stroke="#ff0" stroke-width="3" stroke-linecap="round"/>
                                <ellipse cx="16" cy="24" rx="8" ry="3" fill="none" stroke="#ff0" stroke-width="2"/>
                            </svg>
                        </div>
                        <span class="type-label">Havşa</span>
                        <span class="type-code">G82</span>
                    </button>
                </div>
            </div>

            <!-- Ana SVG Görsel -->
            <svg viewBox="0 0 280 160" class="main-svg">
                <defs>
                    <linearGradient id="parcaTop" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stop-color="#a06848"/><stop offset="100%" stop-color="#805030"/>
                    </linearGradient>
                    <linearGradient id="parcaSide" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stop-color="#604028"/><stop offset="100%" stop-color="#806040"/>
                    </linearGradient>
                    <linearGradient id="drillGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stop-color="#ccc"/><stop offset="100%" stop-color="#666"/>
                    </linearGradient>
                    <filter id="glow"><feGaussianBlur stdDeviation="2" result="b"/>
                        <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
                    </filter>
                </defs>

                <rect width="280" height="160" fill="#060a10"/>

                <!-- Parça İzometrik -->
                <g transform="translate(140, 90)">
                    <ellipse cx="0" cy="45" rx="60" ry="15" fill="rgba(0,0,0,0.3)"/>
                    <path d="M-60,30 L-60,-10 L-30,-25 L-30,15 Z" fill="url(#parcaSide)" stroke="#201510" stroke-width="0.5"/>
                    <path d="M60,30 L60,-10 L30,-25 L30,15 Z" fill="url(#parcaSide)" stroke="#201510" stroke-width="0.5"/>
                    <path d="M-60,-10 L-30,-25 L30,-25 L60,-10 L0,5 Z" fill="url(#parcaTop)" stroke="#201510" stroke-width="0.5"/>

                    <!-- Delikler -->
                    <g *ngFor="let nokta of aktivite.delikNoktalar; let i = index">
                        <ellipse [attr.cx]="getDelikX(i)" cy="-12" rx="8" ry="3"
                                 fill="#030303" [attr.stroke]="seciliDelikIdx === i ? '#0ff' : '#0f0'" stroke-width="1"
                                 (click)="delikSec(i)" class="clickable"/>
                        <text [attr.x]="getDelikX(i)" y="-8" text-anchor="middle" fill="#0f0" font-size="6">{{i + 1}}</text>
                    </g>

                    <!-- Matkap -->
                    <g transform="translate(0, -50)">
                        <rect x="-3" y="0" width="6" height="30" fill="url(#drillGrad)"/>
                        <polygon points="0,35 -4,30 4,30" fill="#888"/>
                        <text x="0" y="-5" text-anchor="middle" fill="#0ff" font-size="5">TAKIM</text>
                    </g>
                </g>

                <!-- Z Değerleri -->
                <g class="clickable" (click)="editValue('guvenliZ')">
                    <rect x="10" y="10" width="50" height="18" rx="3" fill="rgba(0,100,100,0.3)" stroke="#088"/>
                    <text x="35" y="17" text-anchor="middle" fill="#0aa" font-size="6">Güvenli Z</text>
                    <text x="35" y="25" text-anchor="middle" fill="#0ff" font-size="8" font-weight="bold">{{aktivite.guvenliZ}}</text>
                </g>
                <g class="clickable" (click)="editValue('retraktZ')">
                    <rect x="10" y="32" width="50" height="18" rx="3" fill="rgba(0,80,60,0.3)" stroke="#084"/>
                    <text x="35" y="39" text-anchor="middle" fill="#086" font-size="6">Retrakt</text>
                    <text x="35" y="47" text-anchor="middle" fill="#0f8" font-size="8" font-weight="bold">{{aktivite.retraktZ}}</text>
                </g>
                <g class="clickable" (click)="editValue('ustZ')">
                    <rect x="220" y="10" width="50" height="18" rx="3" fill="rgba(0,150,200,0.3)" stroke="#0af"/>
                    <text x="245" y="17" text-anchor="middle" fill="#0af" font-size="6">Üst Z</text>
                    <text x="245" y="25" text-anchor="middle" fill="#0ff" font-size="8" font-weight="bold">{{aktivite.ustZ}}</text>
                </g>
                <g class="clickable" (click)="editValue('altZ')">
                    <rect x="220" y="32" width="50" height="18" rx="3" fill="rgba(200,100,50,0.3)" stroke="#a64"/>
                    <text x="245" y="39" text-anchor="middle" fill="#a64" font-size="6">Alt Z</text>
                    <text x="245" y="47" text-anchor="middle" fill="#f86" font-size="8" font-weight="bold">{{aktivite.altZ}}</text>
                </g>

                <g transform="translate(10, 140)">
                    <text x="0" y="0" fill="#666" font-size="7">Derinlik: {{derinlik | number:'1.1-1'}}mm</text>
                </g>
            </svg>

            <!-- Alt Kontrol -->
            <div class="control-panel">
                <button class="ctrl-btn select-btn" [class.selecting]="secimModu === 'noktaEkle'"
                        (click)="secNoktaEkle()">
                    <svg viewBox="0 0 16 16"><circle cx="8" cy="8" r="3" fill="currentColor"/><path d="M8,2 L8,5 M8,11 L8,14 M2,8 L5,8 M11,8 L14,8" stroke="currentColor" stroke-width="1.5"/></svg>
                    {{secimModu === 'noktaEkle' ? 'Seçim...' : 'Nokta Ekle'}}
                </button>
                <button class="ctrl-btn" (click)="temizleDelikler()">
                    <svg viewBox="0 0 16 16"><path d="M4,4 L12,12 M12,4 L4,12" stroke="currentColor" stroke-width="2"/></svg>
                    Temizle
                </button>
                <button class="ctrl-btn primary" (click)="hesapla()">
                    <svg viewBox="0 0 16 16"><path d="M2,8 L6,12 L14,4" stroke="currentColor" stroke-width="2" fill="none"/></svg>
                    Hesapla
                </button>
            </div>
            <div class="select-hint" *ngIf="secimModu === 'noktaEkle'">
                3D sahnede delik konumlarını tıklayın. ESC: Bitir
            </div>
        </div>

        <!-- TAB 1: TİP PARAMETRELERİ -->
        <div class="tab-content" *ngIf="aktifTab === 1">
            <!-- PUNTA -->
            <div class="params-section" *ngIf="aktivite.delikTipi === 'Punta'">
                <svg viewBox="0 0 280 80" class="param-svg">
                    <rect width="280" height="80" fill="#060a10"/>
                    <g transform="translate(50, 40)">
                        <path d="M0,0 L0,30" stroke="#0ff" stroke-width="4" stroke-linecap="round"/>
                        <path d="M-6,30 L0,40 L6,30" fill="#0ff"/>
                        <text x="0" y="-8" text-anchor="middle" fill="#0aa" font-size="7">PUNTA</text>
                    </g>
                    <rect x="100" y="25" width="160" height="40" fill="url(#parcaTop)" stroke="#402820"/>
                    <path d="M180,25 L180,45 L172,55 L188,55 L180,45" fill="#030303" stroke="#0ff"/>
                </svg>
                <div class="param-grid">
                    <div class="param-item" (click)="editValue('puntaDerinlik')">
                        <span class="p-label">Derinlik</span>
                        <span class="p-value cyan">{{aktivite.puntaDerinlik}} mm</span>
                    </div>
                    <div class="param-item" (click)="cycleParam('puntaAci')">
                        <span class="p-label">Açı</span>
                        <span class="p-value">{{aktivite.puntaAci}}°</span>
                    </div>
                    <div class="param-item" (click)="editValue('dipBekleme')">
                        <span class="p-label">Bekleme</span>
                        <span class="p-value orange">{{aktivite.dipBekleme}} sn</span>
                    </div>
                    <div class="param-item" (click)="editValue('puntaIlerleme')">
                        <span class="p-label">İlerleme</span>
                        <span class="p-value">{{aktivite.puntaIlerleme}} mm/dk</span>
                    </div>
                </div>
            </div>

            <!-- DELME -->
            <div class="params-section" *ngIf="aktivite.delikTipi === 'Delme'">
                <svg viewBox="0 0 280 80" class="param-svg">
                    <rect width="280" height="80" fill="#060a10"/>
                    <g transform="translate(40, 10)">
                        <path d="M15,0 L15,15 M15,20 L15,35 M15,40 L15,55" stroke="#0f8" stroke-width="3" stroke-linecap="round"/>
                        <text x="15" y="68" text-anchor="middle" fill="#086" font-size="7">PECK</text>
                    </g>
                    <rect x="80" y="15" width="180" height="50" fill="url(#parcaTop)" stroke="#402820"/>
                    <rect x="165" y="15" width="10" height="50" fill="#030303"/>
                </svg>
                <div class="param-grid">
                    <div class="param-item" (click)="cycleParam('peckTipi')">
                        <span class="p-label">Tip</span>
                        <span class="p-value green">{{aktivite.peckTipi}}</span>
                    </div>
                    <div class="param-item" (click)="editValue('peckDerinlik')">
                        <span class="p-label">Peck</span>
                        <span class="p-value cyan">{{aktivite.peckDerinlik}} mm</span>
                    </div>
                    <div class="param-item" (click)="editValue('peckRetraktMiktari')">
                        <span class="p-label">Retrakt</span>
                        <span class="p-value">{{aktivite.peckRetraktMiktari}} mm</span>
                    </div>
                    <div class="param-item" (click)="editValue('peckAzaltmaOrani')">
                        <span class="p-label">Azaltma</span>
                        <span class="p-value orange">{{aktivite.peckAzaltmaOrani}}%</span>
                    </div>
                </div>
            </div>

            <!-- RAYBA -->
            <div class="params-section" *ngIf="aktivite.delikTipi === 'Rayba'">
                <svg viewBox="0 0 280 80" class="param-svg">
                    <rect width="280" height="80" fill="#060a10"/>
                    <g transform="translate(40, 10)">
                        <rect x="10" y="0" width="10" height="55" fill="#f80" rx="2"/>
                        <text x="15" y="68" text-anchor="middle" fill="#a64" font-size="7">RAYBA</text>
                    </g>
                    <rect x="80" y="15" width="180" height="50" fill="url(#parcaTop)" stroke="#402820"/>
                    <rect x="165" y="15" width="10" height="50" fill="#030303" stroke="#f80"/>
                </svg>
                <div class="param-grid">
                    <div class="param-item" (click)="editValue('raybaStok')">
                        <span class="p-label">Stok</span>
                        <span class="p-value orange">{{aktivite.raybaStok}} mm</span>
                    </div>
                    <div class="param-item" (click)="editValue('raybaDwellBottom')">
                        <span class="p-label">Bekleme</span>
                        <span class="p-value">{{aktivite.raybaDwellBottom}} sn</span>
                    </div>
                    <div class="param-item" (click)="editValue('raybaIlerleme')">
                        <span class="p-label">İlerleme</span>
                        <span class="p-value cyan">{{aktivite.raybaIlerleme}} mm/dk</span>
                    </div>
                    <div class="param-item" (click)="editValue('raybaCikisHizi')">
                        <span class="p-label">Çıkış</span>
                        <span class="p-value">{{aktivite.raybaCikisHizi}} mm/dk</span>
                    </div>
                </div>
                <div class="info-box">G85: Feed rate ile geri çıkar</div>
            </div>

            <!-- KILAVUZ -->
            <div class="params-section" *ngIf="aktivite.delikTipi === 'Kilavuz'">
                <svg viewBox="0 0 280 80" class="param-svg">
                    <rect width="280" height="80" fill="#060a10"/>
                    <g transform="translate(40, 10)">
                        <path d="M15,0 L15,55" stroke="#f0f" stroke-width="4"/>
                        <path d="M10,8 L20,12 M10,20 L20,24 M10,32 L20,36 M10,44 L20,48" stroke="#f0f" stroke-width="1.5"/>
                        <text x="15" y="68" text-anchor="middle" fill="#808" font-size="7">KILAVUZ</text>
                    </g>
                    <rect x="80" y="15" width="180" height="50" fill="url(#parcaTop)" stroke="#402820"/>
                    <rect x="165" y="15" width="10" height="50" fill="#030303" stroke="#f0f"/>
                </svg>
                <div class="param-grid">
                    <div class="param-item" (click)="cycleParam('disTipi')">
                        <span class="p-label">Tip</span>
                        <span class="p-value magenta">{{aktivite.disTipi}}</span>
                    </div>
                    <div class="param-item" (click)="cycleParam('disCap')">
                        <span class="p-label">Çap</span>
                        <span class="p-value">M{{aktivite.disCap}}</span>
                    </div>
                    <div class="param-item" (click)="editValue('disAdimi')">
                        <span class="p-label">Adım</span>
                        <span class="p-value cyan">{{aktivite.disAdimi}} mm</span>
                    </div>
                    <div class="param-item" (click)="editValue('disDerinlik')">
                        <span class="p-label">Derinlik</span>
                        <span class="p-value orange">{{aktivite.disDerinlik}} mm</span>
                    </div>
                    <div class="param-item" (click)="toggleDisYonu()">
                        <span class="p-label">Yön</span>
                        <span class="p-value" [class.green]="aktivite.disYonu === 'Sag'">{{aktivite.disYonu === 'Sag' ? 'Sağ ↻' : 'Sol ↺'}}</span>
                    </div>
                    <div class="param-item" (click)="editValue('kilavuzDevir')">
                        <span class="p-label">Devir</span>
                        <span class="p-value">{{aktivite.kilavuzDevir}} rpm</span>
                    </div>
                </div>
                <div class="info-box calculated">İlerleme: {{aktivite.disAdimi * aktivite.kilavuzDevir | number:'1.0-0'}} mm/dk</div>
                <div class="toggle-row">
                    <label class="toggle-item" (click)="toggleRigidTapping()">
                        <span class="toggle-box" [class.active]="aktivite.rigidTapping">✓</span>
                        Rigid Tapping
                    </label>
                    <label class="toggle-item" (click)="toggleFloatingHolder()">
                        <span class="toggle-box" [class.active]="aktivite.floatingHolder">✓</span>
                        Floating Holder
                    </label>
                </div>
            </div>

            <!-- HAVŞA -->
            <div class="params-section" *ngIf="aktivite.delikTipi === 'Havsa'">
                <svg viewBox="0 0 280 80" class="param-svg">
                    <rect width="280" height="80" fill="#060a10"/>
                    <g transform="translate(40, 10)">
                        <path d="M15,0 L15,35" stroke="#ff0" stroke-width="4" stroke-linecap="round"/>
                        <ellipse cx="15" cy="45" rx="12" ry="4" fill="none" stroke="#ff0" stroke-width="2"/>
                        <text x="15" y="60" text-anchor="middle" fill="#a80" font-size="7">HAVŞA</text>
                    </g>
                    <rect x="80" y="15" width="180" height="50" fill="url(#parcaTop)" stroke="#402820"/>
                    <ellipse cx="170" cy="20" rx="12" ry="4" fill="#030303" stroke="#ff0"/>
                    <rect x="165" y="20" width="10" height="45" fill="#030303"/>
                </svg>
                <div class="param-grid">
                    <div class="param-item" (click)="editValue('havsaCap')">
                        <span class="p-label">Çap</span>
                        <span class="p-value yellow">{{aktivite.havsaCap}} mm</span>
                    </div>
                    <div class="param-item" (click)="editValue('havsaDerinlik')">
                        <span class="p-label">Derinlik</span>
                        <span class="p-value cyan">{{aktivite.havsaDerinlik}} mm</span>
                    </div>
                    <div class="param-item" (click)="editValue('havsaDwell')">
                        <span class="p-label">Bekleme</span>
                        <span class="p-value orange">{{aktivite.havsaDwell}} sn</span>
                    </div>
                    <div class="param-item" (click)="editValue('havsaIlerleme')">
                        <span class="p-label">İlerleme</span>
                        <span class="p-value">{{aktivite.havsaIlerleme}} mm/dk</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- TAB 2: TAKIM -->
        <div class="tab-content" *ngIf="aktifTab === 2">
            <svg viewBox="0 0 280 100" class="param-svg">
                <rect width="280" height="100" fill="#060a10"/>
                <g transform="translate(140, 50)">
                    <rect x="-8" y="-40" width="16" height="35" fill="url(#drillGrad)" rx="2"/>
                    <polygon points="0,5 -10,-5 10,-5" fill="#888"/>
                    <ellipse cx="0" cy="-40" rx="12" ry="4" fill="#555"/>
                    <line x1="15" y1="-40" x2="15" y2="5" stroke="#0ff" stroke-width="0.5" stroke-dasharray="2,2"/>
                    <text x="22" y="-18" fill="#0ff" font-size="6">{{aktivite.takim?.boy || 50}}mm</text>
                    <line x1="-12" y1="10" x2="12" y2="10" stroke="#0f8" stroke-width="0.5"/>
                    <text x="0" y="20" text-anchor="middle" fill="#0f8" font-size="6">Ø{{aktivite.takim?.cap || 6}}</text>
                </g>
            </svg>
            <div class="param-grid">
                <div class="param-item" (click)="editValue('takimCap')">
                    <span class="p-label">Çap</span>
                    <span class="p-value cyan">{{aktivite.takim?.cap || 6}} mm</span>
                </div>
                <div class="param-item" (click)="editValue('takimBoy')">
                    <span class="p-label">Boy</span>
                    <span class="p-value">{{aktivite.takim?.boy || 50}} mm</span>
                </div>
                <div class="param-item" (click)="editValue('takimKesmeBoy')">
                    <span class="p-label">Kesme</span>
                    <span class="p-value orange">{{aktivite.takim?.kesmeBoy || 30}} mm</span>
                </div>
                <div class="param-item" (click)="cycleParam('takimTipi')">
                    <span class="p-label">Tip</span>
                    <span class="p-value green">{{aktivite.takim?.takimTipi || 'Matkap'}}</span>
                </div>
            </div>
            <div class="param-grid">
                <div class="param-item" (click)="editValue('devir')">
                    <span class="p-label">Devir</span>
                    <span class="p-value magenta">{{aktivite.devir}} rpm</span>
                </div>
                <div class="param-item" (click)="editValue('ilerleme')">
                    <span class="p-label">İlerleme</span>
                    <span class="p-value">{{aktivite.ilerleme}} mm/dk</span>
                </div>
            </div>
        </div>

        <!-- Footer -->
        <div class="d-footer">
            <button class="btn" (click)="iptal()">İptal</button>
            <button class="btn ok" (click)="kaydet()">Tamam</button>
        </div>

        <!-- Edit Popup -->
        <div class="edit-popup" *ngIf="editingField">
            <div class="edit-overlay" (click)="cancelEdit()"></div>
            <div class="edit-box">
                <div class="edit-label">{{editingLabel}}</div>
                <input type="number" class="edit-input" [(ngModel)]="editingValue"
                       (keydown.enter)="applyEdit()" (keydown.escape)="cancelEdit()" autofocus/>
                <div class="edit-actions">
                    <button class="edit-btn" (click)="cancelEdit()">İptal</button>
                    <button class="edit-btn ok" (click)="applyEdit()">Tamam</button>
                </div>
            </div>
        </div>
    </div>
    `,
    styles: [`
        :host { display: block; }
        .cam-dialog {
            width: 260px;
            background: linear-gradient(135deg, #0a1520 0%, #061018 50%, #040810 100%);
            border-radius: 10px;
            box-shadow: 0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05);
            position: relative; overflow: hidden;
            font-family: 'Segoe UI', system-ui, sans-serif;
        }
        .neon-border {
            position: absolute; inset: 0; border-radius: 10px; pointer-events: none;
            border: 1px solid transparent;
            background: linear-gradient(135deg, rgba(0,255,136,0.3), rgba(0,200,255,0.2), rgba(0,255,136,0.1)) border-box;
            -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
            mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
            -webkit-mask-composite: xor; mask-composite: exclude;
        }
        .d-header {
            display: flex; align-items: center; justify-content: space-between;
            padding: 6px 8px; position: relative;
            background: linear-gradient(180deg, rgba(0,60,40,0.3) 0%, rgba(0,30,20,0.2) 100%);
            border-bottom: 1px solid rgba(0,255,136,0.2);
        }
        .header-glow {
            position: absolute; top: 0; left: 0; right: 0; height: 2px;
            background: linear-gradient(90deg, transparent, #0f8, transparent);
        }
        .title-wrap { display: flex; align-items: center; gap: 5px; }
        .title-icon { width: 16px; height: 16px; }
        .title { color: #fff; font-size: 11px; font-weight: 600; }
        .subtitle { color: #0f8; font-size: 8px; background: rgba(0,255,136,0.1); padding: 1px 4px; border-radius: 6px; }
        .close-btn {
            background: none; border: none; color: #666; font-size: 12px; cursor: pointer;
            width: 18px; height: 18px; border-radius: 4px; transition: all 0.2s;
        }
        .close-btn:hover { background: rgba(255,100,100,0.2); color: #f66; }
        .tab-header {
            display: flex; padding: 4px 5px; gap: 3px;
            background: rgba(0,20,30,0.5); border-bottom: 1px solid rgba(0,100,80,0.2);
        }
        .tab-btn {
            flex: 1; display: flex; flex-direction: column; align-items: center; gap: 1px;
            padding: 4px 3px; border: none; border-radius: 5px; cursor: pointer;
            background: transparent; color: #668; font-size: 8px; transition: all 0.2s;
        }
        .tab-btn:hover { background: rgba(0,100,80,0.2); color: #8aa; }
        .tab-btn.active { background: rgba(0,150,100,0.3); color: #0f8; }
        .tab-icon { width: 13px; height: 13px; }
        .tab-content { padding: 5px; min-height: 160px; }
        .type-section { margin-bottom: 5px; }
        .type-buttons { display: flex; gap: 3px; }
        .type-btn {
            flex: 1; display: flex; flex-direction: column; align-items: center; gap: 1px;
            padding: 4px 1px; border: 1px solid rgba(0,100,80,0.3); border-radius: 5px;
            background: rgba(0,30,25,0.5); cursor: pointer; transition: all 0.2s;
        }
        .type-btn:hover { border-color: rgba(0,200,150,0.5); background: rgba(0,50,40,0.5); }
        .type-btn.active { border-color: #0f8; background: rgba(0,100,80,0.3); box-shadow: 0 0 10px rgba(0,255,136,0.3); }
        .type-icon-wrap { width: 22px; height: 22px; }
        .type-icon { width: 100%; height: 100%; }
        .type-label { color: #aaa; font-size: 7px; }
        .type-btn.active .type-label { color: #fff; }
        .type-code { color: #0a8; font-size: 6px; }
        .main-svg, .param-svg { width: 100%; border-radius: 6px; }
        .param-svg { margin-bottom: 5px; }
        .clickable { cursor: pointer; transition: all 0.2s; }
        .clickable:hover { filter: brightness(1.3); }
        .control-panel { display: flex; gap: 4px; padding: 5px 0; }
        .ctrl-btn {
            flex: 1; display: flex; align-items: center; justify-content: center; gap: 3px;
            padding: 5px; border: 1px solid rgba(0,100,80,0.3); border-radius: 5px;
            background: rgba(0,30,25,0.5); color: #8aa; font-size: 9px; cursor: pointer; transition: all 0.2s;
        }
        .ctrl-btn:hover { border-color: rgba(0,200,150,0.5); color: #aee; }
        .ctrl-btn.primary { border-color: #0a8; background: rgba(0,100,80,0.4); color: #0f8; }
        .ctrl-btn.primary:hover { background: rgba(0,150,100,0.5); }
        .ctrl-btn svg { width: 10px; height: 10px; }
        .param-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 4px; margin-bottom: 5px; }
        .param-item {
            display: flex; flex-direction: column; gap: 1px;
            padding: 5px; border: 1px solid rgba(0,100,80,0.2); border-radius: 5px;
            background: rgba(0,30,25,0.4); cursor: pointer; transition: all 0.2s;
        }
        .param-item:hover { border-color: rgba(0,200,150,0.4); background: rgba(0,50,40,0.4); }
        .p-label { color: #668; font-size: 7px; text-transform: uppercase; }
        .p-value { color: #adf; font-size: 10px; font-weight: 600; }
        .p-value.cyan { color: #0ff; }
        .p-value.green { color: #0f8; }
        .p-value.orange { color: #f80; }
        .p-value.yellow { color: #ff0; }
        .p-value.magenta { color: #f0f; }
        .info-box {
            padding: 4px 7px; margin: 5px 0; border-radius: 5px;
            background: rgba(0,60,80,0.3); border-left: 3px solid #0af;
            color: #8cf; font-size: 8px;
        }
        .info-box.calculated { border-left-color: #0f8; color: #8f8; }
        .toggle-row { display: flex; gap: 8px; padding: 5px 0; }
        .toggle-item { display: flex; align-items: center; gap: 4px; color: #8aa; font-size: 9px; cursor: pointer; }
        .toggle-box {
            width: 13px; height: 13px; border: 1px solid #446; border-radius: 3px;
            display: flex; align-items: center; justify-content: center;
            color: transparent; font-size: 9px; transition: all 0.2s;
        }
        .toggle-box.active { background: rgba(0,200,150,0.4); border-color: #0f8; color: #0f8; }
        .d-footer {
            display: flex; gap: 4px; padding: 6px;
            background: linear-gradient(180deg, rgba(0,30,25,0.3) 0%, rgba(0,20,15,0.5) 100%);
            border-radius: 0 0 10px 10px;
        }
        .btn {
            flex: 1; padding: 5px 0; border-radius: 5px;
            background: linear-gradient(135deg, rgba(0,50,40,0.6) 0%, rgba(0,30,25,0.6) 100%);
            border: 1px solid rgba(0,150,100,0.3); color: #adf;
            font-size: 9px; font-weight: 600; cursor: pointer; transition: all 0.25s;
            text-transform: uppercase;
        }
        .btn:hover { transform: translateY(-2px); box-shadow: 0 4px 15px rgba(0,150,100,0.2); }
        .btn.ok {
            background: linear-gradient(135deg, rgba(0,150,100,0.6) 0%, rgba(0,100,80,0.6) 100%);
            border-color: rgba(0,200,150,0.5); color: #fff;
        }
        .edit-popup { position: fixed; inset: 0; display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .edit-overlay { position: absolute; inset: 0; background: rgba(0,0,0,0.7); }
        .edit-box {
            position: relative; width: 180px; padding: 12px;
            background: linear-gradient(135deg, #0a1520, #061018); border-radius: 10px;
            border: 1px solid rgba(0,200,150,0.3); box-shadow: 0 8px 32px rgba(0,0,0,0.5);
        }
        .edit-label { color: #8aa; font-size: 9px; margin-bottom: 6px; text-transform: uppercase; }
        .edit-input {
            width: 100%; padding: 7px; border: 1px solid rgba(0,150,100,0.3); border-radius: 5px;
            background: rgba(0,30,25,0.8); color: #0ff; font-size: 14px; font-weight: 600;
            text-align: center; outline: none;
        }
        .edit-input:focus { border-color: #0f8; box-shadow: 0 0 10px rgba(0,255,136,0.3); }
        .edit-actions { display: flex; gap: 6px; margin-top: 8px; }
        .edit-btn {
            flex: 1; padding: 6px; border: 1px solid rgba(0,100,80,0.3); border-radius: 5px;
            background: rgba(0,30,25,0.5); color: #8aa; font-size: 9px; cursor: pointer;
        }
        .edit-btn.ok { border-color: #0a8; background: rgba(0,100,80,0.4); color: #0f8; }
        .ctrl-btn.select-btn.selecting {
            animation: pulse-select 1.5s infinite;
            border-color: #0f8;
            box-shadow: 0 0 10px rgba(0,255,136,0.5);
        }
        @keyframes pulse-select {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
        }
        .select-hint {
            margin: 5px 6px;
            padding: 4px 7px;
            background: rgba(0,255,136,0.1);
            border: 1px solid rgba(0,255,136,0.3);
            border-radius: 4px;
            font-size: 8px;
            color: #0f8;
            text-align: center;
        }
    `]
})
export class DialogFikoDelikComponent implements OnInit, OnDestroy {
    @Input() aktivite!: FikoAktiviteDelik;
    @Input() anaGosterim: any; // FikoThreeJsComponent referansı
    @Input() pencere: any;
    @Output() onKaydet = new EventEmitter<FikoAktiviteDelik>();
    @Output() onIptal = new EventEmitter<void>();

    aktifTab = 0;
    seciliDelikIdx = -1;
    editingField: string | null = null;
    editingLabel = '';
    editingValue = 0;

    // 3D Seçim State
    secimModu: 'yok' | 'noktaEkle' = 'yok';
    private fikoSecililer: FikoSecililer | null = null;
    private secimSubscriptions: Subscription[] = [];
    private keyDownHandler: ((e: KeyboardEvent) => void) | null = null;

    constructor(private cd: ChangeDetectorRef) {}

    ngOnInit(): void {
        if (!this.aktivite) {
            this.aktivite = new FikoAktiviteDelik();
        }
    }

    get derinlik(): number {
        return (this.aktivite?.ustZ ?? 0) - (this.aktivite?.altZ ?? -10);
    }

    getDelikX(idx: number): number {
        const total = this.aktivite?.delikNoktalar?.length || 1;
        const spacing = 90 / Math.max(total, 1);
        return (idx - (total - 1) / 2) * spacing;
    }

    setTab(idx: number): void {
        this.aktifTab = idx;
        this.cd.detectChanges();
    }

    setDelikTipi(tip: 'Punta' | 'Delme' | 'Rayba' | 'Kilavuz' | 'Havsa'): void {
        if (this.aktivite) {
            this.aktivite.delikTipi = tip;
            if (this.aktivite.takim) {
                const tipMap: Record<string, string> = {
                    'Punta': 'Punta', 'Delme': 'Matkap', 'Rayba': 'Rayba', 'Kilavuz': 'Kilavuz', 'Havsa': 'Havsa'
                };
                this.aktivite.takim.takimTipi = tipMap[tip] || 'Matkap';
            }
            this.cd.detectChanges();
        }
    }

    delikSec(idx: number): void {
        this.seciliDelikIdx = idx;
        this.cd.detectChanges();
    }

    temizleDelikler(): void {
        if (this.aktivite) {
            this.aktivite.delikNoktalar = [];
            this.aktivite.delikCaplar = [];
            this.seciliDelikIdx = -1;
            this.cd.detectChanges();
        }
    }

    // ═══════════════════════════════════════════════════════════════════
    // 3D SEÇİM SİSTEMİ - FikoSecililer Entegrasyonu
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Delik noktası ekleme modunu başlat - 3D vertex/point seçimi
     */
    secNoktaEkle(): void {
        if (this.secimModu === 'noktaEkle') {
            // Seçim modundaysa kapat
            this.secimModunuKapat();
            return;
        }
        this.secimModu = 'noktaEkle';
        this.baslat3DSecim();
        this.cd.detectChanges();
    }

    /**
     * 3D seçim modunu başlat - FikoSecililer kullanarak
     */
    private baslat3DSecim(): void {
        const gosterim = this.anaGosterim || this.pencere?.anaGosterim;
        if (!gosterim?.sahneler?.[0]) {
            console.warn('[Delik Dialog] Sahne bulunamadı');
            return;
        }

        // FikoSecililer'i al
        this.fikoSecililer = gosterim.sahneler[0].secimler;
        if (!this.fikoSecililer) {
            console.warn('[Delik Dialog] FikoSecililer bulunamadı');
            return;
        }

        // Önceki subscription'ları temizle
        this.secimSubscriptionTemizle();

        // FikoSecililer'i yapılandır
        this.fikoSecililer.aktif = true;
        this.fikoSecililer.kaliciSecimModu = true;
        this.fikoSecililer.hoverHighlightEnabled = true;
        this.fikoSecililer.temizle();

        // Delik için yüzey seçimi (nokta koordinatını yüzeyden alacağız)
        this.fikoSecililer.filitreler = ['Mesh', 'Points'];

        // Tıklama event'ini dinle
        const tiklandiSub = this.fikoSecililer.tiklandi.subscribe((kesisim: any) => {
            if (kesisim) {
                this.handleSecimKesisim(kesisim);
            }
        });
        this.secimSubscriptions.push(tiklandiSub);

        // Hover event'ini dinle (görsel feedback için)
        const uzerindeSub = this.fikoSecililer.uzerinde.subscribe((kesisim: any) => {
            if (kesisim && gosterim.ciz) {
                gosterim.ciz();
            }
        });
        this.secimSubscriptions.push(uzerindeSub);

        // Keyboard handler (ESC kapatır)
        this.keyDownHandler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                this.secimModunuKapat();
            }
        };
        document.addEventListener('keydown', this.keyDownHandler);

        // Cursor değiştir
        const canvas = gosterim?.sunucu?.domElement as HTMLCanvasElement;
        if (canvas) {
            canvas.style.cursor = 'crosshair';
        }
    }

    /**
     * Seçim kesişimini işle - Delik noktası ekle
     */
    private handleSecimKesisim(kesisim: any): void {
        if (!kesisim || !this.aktivite) return;

        const point = kesisim.point?.clone() || new THREE.Vector3();

        // Delik noktası ekle
        const delikNokta = new FikoVektor3D(point.x, point.y, point.z);
        if (!this.aktivite.delikNoktalar) {
            this.aktivite.delikNoktalar = [];
        }
        if (!this.aktivite.delikCaplar) {
            this.aktivite.delikCaplar = [];
        }

        this.aktivite.delikNoktalar.push(delikNokta);
        this.aktivite.delikCaplar.push(this.aktivite.takim?.cap || 10);

        // Z limitlerini güncelle (ilk nokta için)
        if (this.aktivite.delikNoktalar.length === 1) {
            this.aktivite.ustZ = point.z;
            if (this.aktivite.altZ >= point.z) {
                this.aktivite.altZ = point.z - 10; // Varsayılan derinlik
            }
        }

        this.cd.detectChanges();
    }

    /**
     * Seçim modunu kapat ve temizle
     */
    private secimModunuKapat(): void {
        const gosterim = this.anaGosterim || this.pencere?.anaGosterim;

        // Keyboard handler'ı kaldır
        if (this.keyDownHandler) {
            document.removeEventListener('keydown', this.keyDownHandler);
            this.keyDownHandler = null;
        }

        // Cursor'u normale döndür
        const canvas = gosterim?.sunucu?.domElement as HTMLCanvasElement;
        if (canvas) {
            canvas.style.cursor = 'default';
        }

        // FikoSecililer'i deaktive et
        if (this.fikoSecililer) {
            this.fikoSecililer.aktif = false;
            this.fikoSecililer.temizle();
            this.fikoSecililer = null;
        }

        // Subscription'ları temizle
        this.secimSubscriptionTemizle();

        this.secimModu = 'yok';
        this.cd.detectChanges();
    }

    /**
     * Subscription'ları temizle
     */
    private secimSubscriptionTemizle(): void {
        this.secimSubscriptions.forEach(sub => {
            if (sub && !sub.closed) {
                sub.unsubscribe();
            }
        });
        this.secimSubscriptions = [];
    }

    ngOnDestroy(): void {
        this.secimModunuKapat();
    }

    hesapla(): void {
        if (this.aktivite) {
            this.aktivite.takimYoluHesapla();
            this.cd.detectChanges();
        }
    }

    editValue(field: string): void {
        this.editingField = field;
        const labels: Record<string, string> = {
            'guvenliZ': 'Güvenli Z (mm)', 'retraktZ': 'Retrakt Z (mm)', 'ustZ': 'Üst Z (mm)', 'altZ': 'Alt Z (mm)',
            'puntaDerinlik': 'Punta Derinliği (mm)', 'dipBekleme': 'Dip Bekleme (sn)', 'puntaIlerleme': 'İlerleme (mm/dk)',
            'peckDerinlik': 'Peck Derinliği (mm)', 'peckRetraktMiktari': 'Retrakt (mm)', 'peckAzaltmaOrani': 'Azaltma (%)',
            'minPeckDerinlik': 'Min Peck (mm)', 'peckBekleme': 'Bekleme (sn)',
            'raybaStok': 'Stok (mm)', 'raybaDwellBottom': 'Bekleme (sn)', 'raybaIlerleme': 'İlerleme (mm/dk)', 'raybaCikisHizi': 'Çıkış (mm/dk)',
            'disAdimi': 'Adım (mm)', 'disDerinlik': 'Derinlik (mm)', 'kilavuzDevir': 'Devir (rpm)',
            'havsaCap': 'Havşa Çapı (mm)', 'havsaDerinlik': 'Derinlik (mm)', 'havsaDwell': 'Bekleme (sn)', 'havsaIlerleme': 'İlerleme (mm/dk)',
            'takimCap': 'Çap (mm)', 'takimBoy': 'Boy (mm)', 'takimKesmeBoy': 'Kesme (mm)',
            'devir': 'Devir (rpm)', 'ilerleme': 'İlerleme (mm/dk)'
        };
        this.editingLabel = labels[field] || field;
        this.editingValue = this.getFieldValue(field);
        this.cd.detectChanges();
    }

    private getFieldValue(field: string): number {
        if (!this.aktivite) return 0;
        if (field.startsWith('takim') && field !== 'takimTipi') {
            const subField = field.replace('takim', '').toLowerCase();
            return (this.aktivite.takim as any)?.[subField] ?? 0;
        }
        return (this.aktivite as any)[field] ?? 0;
    }

    applyEdit(): void {
        if (!this.aktivite || !this.editingField) return;
        const field = this.editingField;
        if (field.startsWith('takim') && field !== 'takimTipi' && this.aktivite.takim) {
            const subField = field.replace('takim', '').toLowerCase();
            (this.aktivite.takim as any)[subField] = this.editingValue;
        } else {
            (this.aktivite as any)[field] = this.editingValue;
        }
        this.editingField = null;
        this.cd.detectChanges();
    }

    cancelEdit(): void {
        this.editingField = null;
        this.cd.detectChanges();
    }

    cycleParam(field: string): void {
        if (!this.aktivite) return;
        const cycles: Record<string, any[]> = {
            'puntaAci': [60, 90, 118, 120],
            'peckTipi': ['Standart', 'YuksekHiz', 'TamRetrakt'],
            'disTipi': ['Metrik', 'Whitworth', 'UNC', 'UNF'],
            'disCap': [3, 4, 5, 6, 8, 10, 12, 14, 16, 20],
            'takimTipi': ['Matkap', 'Punta', 'Rayba', 'Kilavuz', 'Havsa']
        };
        const opts = cycles[field];
        if (!opts) return;
        let current: any;
        if (field === 'takimTipi' && this.aktivite.takim) {
            current = this.aktivite.takim.takimTipi;
            const idx = opts.indexOf(current);
            this.aktivite.takim.takimTipi = opts[(idx + 1) % opts.length];
        } else {
            current = (this.aktivite as any)[field];
            const idx = opts.indexOf(current);
            (this.aktivite as any)[field] = opts[(idx + 1) % opts.length];
        }
        this.cd.detectChanges();
    }

    toggleDisYonu(): void {
        if (this.aktivite) {
            this.aktivite.disYonu = this.aktivite.disYonu === 'Sag' ? 'Sol' : 'Sag';
            this.cd.detectChanges();
        }
    }

    toggleRigidTapping(): void {
        if (this.aktivite) {
            this.aktivite.rigidTapping = !this.aktivite.rigidTapping;
            this.cd.detectChanges();
        }
    }

    toggleFloatingHolder(): void {
        if (this.aktivite) {
            this.aktivite.floatingHolder = !this.aktivite.floatingHolder;
            this.cd.detectChanges();
        }
    }

    kapat(): void { this.onIptal.emit(); }
    iptal(): void { this.onIptal.emit(); }
    kaydet(): void {
        if (this.aktivite) {
            this.aktivite.takimYoluHesapla();
            this.onKaydet.emit(this.aktivite);
        }
    }
}
